/**
 * Teléfonos de proveedores, fiduciantes y compradores (Mati, 2026-10-19). El backend los guarda en un solo formato
 * (`+549` + característica + abonado, `app/core/telefonos.py`); acá se muestran en grupos y se arman los links. Los
 * links toleran también lo cargado antes de normalizar.
 */
const digitos = (s: string) => s.replace(/\D/g, "");

const MIN_DIGITOS_TELEFONO = 8;

/** Link `tel:`: los dígitos, con `+` adelante si el número se cargó con `+`. Null si tiene menos de 8 dígitos. */
export function linkTelefono(tel: string | null): string | null {
  const d = digitos(tel ?? "");
  if (d.length < MIN_DIGITOS_TELEFONO) return null;
  return (tel ?? "").trim().startsWith("+") ? `tel:+${d}` : `tel:${d}`;
}

/** El teléfono como se guarda (`+5491112345678`, `app/core/telefonos.py:normalizar_telefono`) en grupos, como lo
 * escribe Mati: "+54 9 11 1234 5678", "+54 9 351 555 5555" (característica de 3 si no es 11). Lo que no tiene ese
 * formato, como vino. */
export function formatTelefono(tel: string | null): string {
  if (!tel) return "—";
  const m = /^\+549(\d{10})$/.exec(tel);
  if (!m?.[1]) return tel;
  const n = m[1];
  const area = n.startsWith("11") ? 2 : 3;
  return `+54 9 ${n.slice(0, area)} ${n.slice(area, -4)} ${n.slice(-4)}`;
}

/** Saca el "15" de un celular cargado a la vieja ("11 15 5555-5555"): 12 dígitos = característica (2 a 4) + 15 + abonado
 * (10 − característica). Sólo si hay una única posición posible; si no, lo deja como está. */
function sinQuince(nacional: string): string {
  if (nacional.length !== 12) return nacional;
  const posibles = [2, 3, 4].filter((a) => nacional.slice(a, a + 2) === "15");
  const [a] = posibles;
  return posibles.length === 1 && a !== undefined ? nacional.slice(0, a) + nacional.slice(a + 2) : nacional;
}

/** Link de WhatsApp (`https://wa.me/<dígitos>`), pensado para celulares argentinos:
 * - Cargado con `+` y sin 54 (extranjero): los dígitos tal cual.
 * - Empieza con 54: tal cual; si es 54 + 10 dígitos (sin el 9 de celular), se le agrega el 9.
 * - Si no (número local): se saca un 0 inicial de la característica y el 15 cuando no es ambiguo, y se antepone 549.
 * Null si tiene menos de 8 dígitos. Un celular cargado sin característica ("15 5555-5555") no se puede adivinar. */
export function linkWhatsapp(tel: string | null): string | null {
  const crudo = (tel ?? "").trim();
  const d = digitos(crudo);
  if (d.length < MIN_DIGITOS_TELEFONO) return null;
  if (d.startsWith("54")) {
    const resto = d.slice(2);
    return `https://wa.me/${!resto.startsWith("9") && resto.length === 10 ? `549${resto}` : d}`;
  }
  if (crudo.startsWith("+")) return `https://wa.me/${d}`;
  return `https://wa.me/549${sinQuince(d.replace(/^0/, ""))}`;
}
